import React, { useState } from 'react';

// Upcoming-arrivals import (XLSX) — Front Office → Reservations.
// 1. Download the property's template  2. Upload the filled file → every row
// is checked (room type, rate plan, dates, duplicates, tariff)  3. Import the
// valid rows; each is created through the normal reservation API rules.
const fmtMoney = (n) => (n == null ? '-' : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDmy = (ymd) => {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (ymd || '-');
};

// .xlsx = ZIP package ("PK\x03\x04"); old .xls = OLE2 (D0 CF 11 E0).
// Quick client-side check — the server re-checks the full package.
const checkXlsxSignature = async (file) => {
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  if (head[0] === 0xD0 && head[1] === 0xCF && head[2] === 0x11 && head[3] === 0xE0) {
    return 'This is an old Excel 97-2003 (.xls) file renamed to .xlsx. Open it in Excel and Save As → Excel Workbook (*.xlsx).';
  }
  if (!(head[0] === 0x50 && head[1] === 0x4B && head[2] === 0x03 && head[3] === 0x04)) {
    return 'This is not a real Excel .xlsx file (it may be CSV/text or another file renamed). Save it as Excel Workbook (*.xlsx).';
  }
  return '';
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
  reader.onerror = () => reject(new Error('Could not read the file'));
  reader.readAsDataURL(file);
});

function ArrivalsImportModal({ headers, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [fileB64, setFileB64] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [sendSms, setSendSms] = useState(false);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);

  const downloadTemplate = async () => {
    setError('');
    setBusy('template');
    try {
      const res = await fetch('/api/v1/pms/reservations/import/template', { headers: headers() });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `Template download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Arrivals_Import_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    setPreview(null);
    setResult(null);
    setError('');
    if (!f) return;
    setFile(null);
    setFileB64('');
    if (!/\.xlsx$/i.test(f.name)) {
      const ext = (f.name.match(/\.([a-z0-9]+)$/i) || [])[1];
      setError(`Only .xlsx files are allowed${ext ? ` (this file is .${ext.toLowerCase()})` : ''}. Save it as "Excel Workbook (*.xlsx)" and upload again.`);
      return;
    }
    if (!f.size) { setError('The file is empty'); return; }
    if (f.size > 6 * 1024 * 1024) { setError('File is too large (max 6 MB)'); return; }
    try {
      const sigError = await checkXlsxSignature(f);
      if (sigError) { setError(sigError); return; }
      const b64 = await fileToBase64(f);
      setFile(f);
      setFileB64(b64);
      await validate(b64, f.name);
    } catch (err) {
      setError(err.message);
    }
  };

  const validate = async (b64 = fileB64, fileName = file?.name) => {
    setBusy('validate');
    setError('');
    try {
      const res = await fetch('/api/v1/pms/reservations/import/validate', {
        method: 'POST', headers: headers(), body: JSON.stringify({ file_base64: b64, file_name: fileName })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Validation failed (${res.status})`);
      setPreview(d);
      setShowOnlyErrors(d.invalid > 0 && d.valid === 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const commit = async () => {
    if (!preview?.valid) return;
    const msg = preview.invalid
      ? `Import ${preview.valid} valid booking(s)? ${preview.invalid} row(s) with errors will be skipped.`
      : `Import ${preview.valid} booking(s)?`;
    if (!window.confirm(msg)) return;
    setBusy('commit');
    setError('');
    try {
      const res = await fetch('/api/v1/pms/reservations/import/commit', {
        method: 'POST', headers: headers(), body: JSON.stringify({ file_base64: fileB64, file_name: file?.name, send_sms: sendSms })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Import failed (${res.status})`);
      setResult(d);
      if (d.created > 0 && onImported) onImported(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const reset = () => { setFile(null); setFileB64(''); setPreview(null); setResult(null); setError(''); };

  const th = { padding: '0.45rem 0.5rem', textAlign: 'left', fontSize: '0.74rem', fontWeight: 700, color: '#475569', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', position: 'sticky', top: 0 };
  const td = { padding: '0.4rem 0.5rem', fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
  const btn = (bg, fg = '#fff') => ({ padding: '0.5rem 0.9rem', border: 'none', borderRadius: 6, background: bg, color: fg, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' });
  const rows = (preview?.rows || []).filter((r) => !showOnlyErrors || !r.ok);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 'min(1100px, 100%)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 48px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '0.9rem 1.1rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--theme-primary-dark, #1e3a5f)' }}>Import Upcoming Arrivals (Excel)</div>
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>One row per booking. Rows are checked first; only valid rows are created.</div>
          </div>
          <button onClick={onClose} disabled={busy === 'commit'} style={{ ...btn('#e2e8f0', '#334155'), padding: '0.35rem 0.7rem' }}>✕</button>
        </div>

        <div style={{ padding: '0.9rem 1.1rem', overflow: 'auto', flex: 1 }}>
          {/* Step 1 + 2 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
            <button onClick={downloadTemplate} disabled={!!busy} style={btn('#0f766e')}>
              {busy === 'template' ? 'Preparing…' : '⭳ 1. Download Template'}
            </button>
            <label style={{ ...btn('#2563eb'), display: 'inline-block', opacity: busy ? 0.6 : 1, pointerEvents: busy ? 'none' : 'auto' }}>
              {busy === 'validate' ? 'Checking…' : '⭱ 2. Upload Filled File'}
              <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onPickFile} style={{ display: 'none' }} />
            </label>
            {file && <span style={{ fontSize: '0.8rem', color: '#334155' }}>📄 {file.name}</span>}
            {file && !result && <button onClick={() => validate()} disabled={!!busy} style={btn('#e2e8f0', '#334155')}>↻ Re-check</button>}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.75rem' }}>
            The template lists this property's Room Types, Rate Plans, Companies and Travel Agents. Leave <b>Rate per Night</b> blank to use the rate plan tariff.
          </div>

          {error && <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, padding: '0.55rem 0.75rem', fontSize: '0.84rem', marginBottom: '0.75rem' }}>{error}</div>}

          {/* Result after import */}
          {result && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ background: result.failed ? '#fffbeb' : '#f0fdf4', border: `1px solid ${result.failed ? '#fde68a' : '#bbf7d0'}`, borderRadius: 8, padding: '0.65rem 0.8rem', fontSize: '0.86rem', marginBottom: '0.6rem' }}>
                ✅ <b>{result.created}</b> booking(s) created
                {result.failed ? <> · ⚠ <b>{result.failed}</b> failed</> : null}
                {result.skipped ? <> · {result.skipped} row(s) skipped (errors)</> : null}
              </div>
              <div style={{ overflow: 'auto', maxHeight: '45vh', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Row</th><th style={th}>Guest</th><th style={th}>Arrival</th><th style={th}>Result</th></tr></thead>
                  <tbody>
                    {result.results.map((r) => (
                      <tr key={`r${r.row_no}`}>
                        <td style={td}>{r.row_no}</td>
                        <td style={td}>{r.guest_name}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDmy(r.arrival_date)}</td>
                        <td style={{ ...td, color: r.ok ? '#166534' : '#b91c1c', fontWeight: 600 }}>{r.ok ? `Created ${r.reservation_no}` : r.error}</td>
                      </tr>
                    ))}
                    {(result.skipped_rows || []).map((r) => (
                      <tr key={`s${r.row_no}`}>
                        <td style={td}>{r.row_no}</td>
                        <td style={td}>{r.guest_name || '-'}</td>
                        <td style={td}>-</td>
                        <td style={{ ...td, color: '#92400e' }}>Skipped: {r.errors.join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && !result && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', marginBottom: '0.55rem', fontSize: '0.84rem' }}>
                <span style={{ background: '#f1f5f9', borderRadius: 999, padding: '0.2rem 0.65rem' }}>Rows: <b>{preview.total}</b></span>
                <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 999, padding: '0.2rem 0.65rem' }}>Ready: <b>{preview.valid}</b></span>
                <span style={{ background: preview.invalid ? '#fee2e2' : '#f1f5f9', color: preview.invalid ? '#991b1b' : '#475569', borderRadius: 999, padding: '0.2rem 0.65rem' }}>With errors: <b>{preview.invalid}</b></span>
                {preview.invalid > 0 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
                    <input type="checkbox" checked={showOnlyErrors} onChange={(e) => setShowOnlyErrors(e.target.checked)} /> Show only rows with errors
                  </label>
                )}
              </div>
              <div style={{ overflow: 'auto', maxHeight: '48vh', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Row</th><th style={th}>Status</th><th style={th}>Guest</th><th style={th}>Arrival</th><th style={th}>Departure</th>
                      <th style={th}>Room Type × Rooms</th><th style={th}>Pax/Room</th><th style={th}>Rate Plan</th>
                      <th style={{ ...th, textAlign: 'right' }}>Rate/Night</th><th style={{ ...th, textAlign: 'right' }}>Total</th>
                      <th style={{ ...th, textAlign: 'right' }}>Advance</th><th style={th}>Source / Bill To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.row_no} style={{ background: r.ok ? '#fff' : '#fff7f7' }}>
                        <td style={td}>{r.row_no}</td>
                        <td style={td}>
                          {r.ok
                            ? <span style={{ color: '#166534', fontWeight: 700 }}>✓ Ready</span>
                            : <span style={{ color: '#b91c1c', fontWeight: 700 }}>✕ Error</span>}
                          {!r.ok && <div style={{ color: '#b91c1c', fontSize: '0.74rem', marginTop: 2, minWidth: 220 }}>{r.errors.map((e) => <div key={e}>• {e}</div>)}</div>}
                          {r.warnings?.length > 0 && <div style={{ color: '#92400e', fontSize: '0.74rem', marginTop: 2 }}>{r.warnings.map((w) => <div key={w}>⚠ {w}</div>)}</div>}
                        </td>
                        <td style={td}>{r.summary.guest_name || '-'}{r.summary.external_ref ? <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Ref {r.summary.external_ref}</div> : null}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDmy(r.summary.arrival_date)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDmy(r.summary.departure_date)}{r.summary.nights ? <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{r.summary.nights} night(s)</div> : null}</td>
                        <td style={td}>{r.summary.room_type || '-'} × {r.summary.rooms}</td>
                        <td style={td}>{r.summary.adults}A{r.summary.children ? ` + ${r.summary.children}C` : ''}</td>
                        <td style={td}>{r.summary.rate_plan || '-'}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(r.summary.rate_per_night)}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtMoney(r.summary.total_amount)}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.summary.advance_amount ? fmtMoney(r.summary.advance_amount) : '-'}</td>
                        <td style={td}>
                          {r.summary.booking_source}<div style={{ fontSize: '0.72rem', color: '#64748b' }}>{r.summary.bill_to}{r.summary.company ? ` · ${r.summary.company}` : ''}{r.summary.travel_agent ? ` · TA ${r.summary.travel_agent}` : ''}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '0.75rem 1.1rem', borderTop: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#334155' }}>
            <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} disabled={!!result} />
            Send booking confirmation SMS to guests
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {result
              ? <>
                  <button onClick={reset} style={btn('#e2e8f0', '#334155')}>Import another file</button>
                  <button onClick={onClose} style={btn('#1e3a5f')}>Done</button>
                </>
              : <>
                  <button onClick={onClose} disabled={busy === 'commit'} style={btn('#e2e8f0', '#334155')}>Cancel</button>
                  <button onClick={commit} disabled={!preview?.valid || !!busy} style={{ ...btn('#16a34a'), opacity: !preview?.valid || busy ? 0.55 : 1 }}>
                    {busy === 'commit' ? 'Importing…' : `3. Import ${preview?.valid || 0} Booking(s)`}
                  </button>
                </>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ArrivalsImportModal;
